/* eslint-disable object-curly-newline */
/* eslint-disable no-console */
import { 
	ApplicationInsights, 
	SeverityLevel, 
	RemoteDependencyData
} from "@microsoft/applicationinsights-web";
import { generateW3CId } from "@microsoft/applicationinsights-core-js";

function LoggingService(config) {
	const service = this;

	service.appInsights = null;
	service.filters = [];
	const LEVELS = {
		Warning: "warn",
		Info: "info",
		Critical: "critical",
		Error: "error",
		Verbose: "verbose",
		Debug: "debug",

		NotImplemented: "not-implemented",
		PageView: "page-view",
		Event: "event",
		Object: "object",
		Snapshot: "snapshot"
	};

	function initialize() {
		setupAi();
		setupKeyboardEventCapture();
	}

	function setupAi() {
		service.appInsights = new ApplicationInsights({
			config: {
				connectionString: `InstrumentationKey=${config.appInsights};IngestionEndpoint=https://buoy.bluefindata.com;`,
				loggingLevelConsole: 1, 
				enableDebug: false,
				verboseLogging: false,
				enableCorsCorrelation: true,
				maxAjaxCallsPerView: -1,
				autoTrackPageVisitTime: true
			}
		});

		service.appInsights.loadAppInsights();
		for(const initializer of telemetryInitializers())
			service.appInsights.addTelemetryInitializer(initializer);

		service.filters.push((level, message) => message == null || !(message instanceof String) ? true : !(message.indexOf("WDS") > -1));
		service.filters.push((level, message) => message == null || !(message instanceof String) ? true : !(message.indexOf("HMR") > -1));
		service.filters.push((level) => level != LEVELS.PageView || window.vesl && window.vesl.enablePageViewConsole);
		service.filters.push((level) => level != LEVELS.Event || window.vesl && window.vesl.enableEventConsole);
		service.filters.push((level) => level != LEVELS.Verbose || window.vesl && window.vesl.enableVerboseConsole);
	}

	function setupKeyboardEventCapture() { 
		const Mousetrap = window.Mousetrap;

		const telemetryCallback = (keys, originalCallback) => () => {
			service.event("web-ux-keyboard-shortcut", null, { shortcut: keys });

			originalCallback();
		};

		const originalBind = Mousetrap.bind;
		
		function interceptedBind(
			keys, 
			callback, 
			action, 
			...otherBindArgs
		) {
			const loggingCallback = telemetryCallback(keys, callback);

			originalBind.apply(Mousetrap, [keys, loggingCallback, action, ...otherBindArgs]);
		}
		
		Mousetrap.bind = interceptedBind.bind(Mousetrap);
	}

	function telemetryInitializers() {
		const excludeWebpackTelemetryInitializer = function(envelope) {
			if(envelope.name === RemoteDependencyData.envelopeType) {
				if(envelope.baseData.name.indexOf("hot-update.json") > -1)
					return false;
				if(envelope.baseData.name.indexOf("sockjs-node") > -1)
					return false;
			}
			return true;
		};

		const coerceStatusCodesToSuccess = function(envelope) {
			const migratees = [0, 400, 401, 403, 404];
			if(envelope.name === RemoteDependencyData.envelopeType) {
				if(!envelope.baseData.success && migratees.indexOf(envelope.baseData.responseCode) > -1)
					envelope.baseData.success = true;
			}
		};

		return [excludeWebpackTelemetryInitializer, coerceStatusCodesToSuccess];
	}

	function getCircularReplacer() {
		const seen = new WeakSet();

		return (key, value) => {
			if(typeof value === "object" && value !== null) {
				if(seen.has(value)) {
					return;
				}
				seen.add(value);
			}
			return value;
		};
	}

	function simplify(...rest) {
		if(!rest || rest.length == 0)
			return null;

		if(rest.length > 1)
			service.debug("Passed multiple properties for logged AI method.  Only first property is ever used.");

		const properties = rest[0];
		if(typeof properties != "object")
			return null;

		const empty = {};
		let simplified = empty;
		for(const key in properties) {
			// eslint-disable-next-line no-prototype-builtins
			if(Object.hasOwnProperty(key))
				continue;

			if(typeof properties[key] !== "object" && properties[key] !== undefined && properties[key] !== null && properties[key] !== undefined) {
				simplified = { ...simplified, [key]: properties[key] };
			}
		}

		return simplified === empty ? null : simplified;
	}

	function shouldLog(level, message) {
		for(const filter of service.filters) {
			if(!filter(level, message)) {
				return false;
			}
		}

		return true;
	}

	function processMessage(level, message, ...properties) {
		if(!shouldLog(level, message))
			return;

		let config = null;
		switch(level) {
			case LEVELS.Warning:
				config = { severity: SeverityLevel.Warning, method: "log", color: "#000", background: "#eed202" };
				break;
			case LEVELS.Info:
				config = { severity: SeverityLevel.Information, method: "info", color: "#fff", background: "#44e" };
				break;
			case LEVELS.Critical:
				config = { severity: SeverityLevel.Critical, method: "error", color: "#fff", background: "#ff0000" };
				break;
			case LEVELS.Error:
				config = { severity: SeverityLevel.Error, method: "error", color: "#000", background: "#f95" };
				break;
			case LEVELS.Trace:
			case LEVELS.Verbose:
				config = { severity: SeverityLevel.Verbose, method: "log", color: "#000", background: "#ccc" };
				break;
			case LEVELS.NotImplemented:
				config = { severity: SeverityLevel.Error, method: "error", color: "#fff", background: "#f70" };
				break;
			case LEVELS.Object:
			case LEVELS.Snapshot:
				config = { method: "log", color: "#fff", background: "#000" };
				break;
			default:
				config = { method: "log", color: "#fff", background: "#808" };
		}

		console[config.method](`%c[${level}]%c ${message}`.trimRight(), `color: ${config.color}; background-color: ${config.background};`, "", ...properties);

		if(config.severity !== undefined) {
			if(!(message instanceof String || typeof message === "string")) {
				service.debug("Passed non-string message parameter for logged AI method.  Suppressing any outgoing AI log.");
			}
			else {
				const simplified = simplify(...properties);
				service.appInsights.trackTrace({ 
					message, 
					severityLevel: config.severity, 
					properties: simplified 
				});
			}
		}
	}

	//core logging methods (ordered by severity desc)
	service.critical = (message, ...properties) => processMessage(LEVELS.Critical, message, ...properties);
	service.error = (message, ...properties) => processMessage(LEVELS.Error, message, ...properties);
	service.warn = (message, ...properties) => processMessage(LEVELS.Warning, message, ...properties);
	service.info = (message, ...properties) => processMessage(LEVELS.Info, message, ...properties);
	service.trace = service.verbose = (message, ...properties) => processMessage(LEVELS.Verbose, message, ...properties);
	service.debug = (message, ...properties) => processMessage(LEVELS.Debug, message, ...properties);
	service.debugWhen = (condition, message, ...properties) => condition() ? service.debug(message, ...properties) : null;

	service.missing = (message, ...properties) => processMessage(LEVELS.NotImplemented, message, ...properties);
	service.snapshot = (message, ...objs) => processMessage(LEVELS.Snapshot, message, ...(objs.map(obj => !!obj ? JSON.parse(JSON.stringify(obj, getCircularReplacer())) : "empty object")));
	service.object = (...objs) => processMessage(LEVELS.Object, null, ...objs);

	service.exception = (e, message, ...properties) => {
		const hasMessage = message instanceof String || typeof message === "string";
		const exceptionMessage = hasMessage
			? message.toString()
			: e?.message || (e instanceof String || typeof e === "string" ? e.toString() : "An unexpected exception occurred.");
		const consoleProperties = !hasMessage && message !== undefined
			? [message, ...properties]
			: properties;
		console.error(`%c[${LEVELS.Error}]%c ${exceptionMessage}`, "color: #000; background-color: #f95;", "", e, ...consoleProperties);

		const simplified = simplify(...properties);
		service.appInsights.trackException({ 
			exception: e, 
			properties: simplified
		});
	};

	service.view = (name, uri, properties) => {
		const telemetryContext = service.appInsights.context.telemetryTrace;
		const from = telemetryContext.name;

		if(telemetryContext.name != name) {
			properties = { ...properties, from };

			const traceId = generateW3CId();
			telemetryContext.traceID = traceId;
			telemetryContext.name = name;
		}

		processMessage(LEVELS.PageView, name, properties);

		const simplified = simplify(properties);
		service.appInsights.trackPageView({ 
			name: name,
			uri: uri, 
			refUri: from,
			properties: simplified 
		});
	};

	service.buildWebEvent = (module) => (action) => (name) => `@vesl/web-${module}-${action}-${name}`;
	service.buildReportEvent = this.buildWebEvent("report");
	service.reportDetailEventBuilder = this.buildReportEvent("detail");

	service.event = (name, measurements, properties) => {
		const parser = /([a-zA-Z]+)-([a-zA-Z]+)-([a-zA-Z]+)-([a-zA-Z]+)/;
		const payload = parser.exec(name);

		properties = { ...simplify(properties), product: payload[1], module: payload[2], action: payload[3], event: payload[4] };
		const diagnostics = !(properties || measurements)
			? null
			: {
				...{ properties },
				...(measurements ? { measurements } : null)
			};

		processMessage(LEVELS.Event, name, diagnostics);

		if(!!measurements) {
			properties.measurements = measurements;
		}

		service.appInsights.trackEvent({ name, properties });
	};

	service.time = console.time;
	service.timeEnd = console.timeEnd;
	service.timeLog = console.timeLog;
	service.timeStamp = console.timeStamp;

	service.group = (...args) => console.group(...args);
	service.groupEnd = (...args) => console.groupEnd(...args);
	service.groupCollapsed = (...args) => console.groupCollapsed(...args);

	service.user = function(userId) {
		if(userId)
			service.appInsights.setAuthenticatedUserContext(userId.toString());
		else
			service.appInsights.clearAuthenticatedUserContext();
	};

	service.table = (...args) => console.table ? console.table(...args) : (() => {});

	initialize();

	return service;
}

export default angular
	.module("vesl.services.logging", [])
	.service("$logger", LoggingService).name;
